import { useState, useRef, useEffect } from 'react';
import { useStreamingChat } from '../../../hooks/useStreamingChat';
import { MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { TypingIndicator } from './TypingIndicator';
import { getPersonalInfo } from '../../../config/configLoader';
import { useTheme } from '../../../contexts/ThemeContext';

export const ChatBox = () => {
  const [input, setInput] = useState('');
  const [userEngaged, setUserEngaged] = useState(false);
  const { messages, isLoading, error, sendMessage, stopAnswering } = useStreamingChat();
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const lastMessageCountRef = useRef(messages.length);
  const { isDark } = useTheme();
  
  const personalInfo = getPersonalInfo();
  const firstName = personalInfo?.name.split(' ')[0] || 'AI';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (!userEngaged) {
      lastMessageCountRef.current = messages.length;
      return;
    }
    if (messages.length > lastMessageCountRef.current) {
      scrollToBottom();
    }
    lastMessageCountRef.current = messages.length;
  }, [messages, userEngaged]);

  const handleSubmit = async (message) => {
    setUserEngaged(true);
    await sendMessage(message);
  };

  return (
    <div className="w-full max-w-2xl mx-auto mt-4 mb-16 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`
          rounded-xl overflow-hidden border-gradient
          ${isDark 
            ? 'glass-futuristic' 
            : 'glass-futuristic-light'
          }
        `}
      >
        {/* Chat Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${
          isDark ? 'border-neutral-800' : 'border-neutral-100'
        }`}>
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-md ${isDark ? 'bg-accent-500/10' : 'bg-accent-50'}`}>
              <MessageCircle className="w-4 h-4 text-accent-500" />
            </div>
            <span className={`text-sm font-medium ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
              Chat with {firstName}'s AI
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className={`text-xs ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>Online</span>
          </div>
        </div>

        {/* Messages */}
        <div
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto space-y-3 p-4 max-h-[320px]"
        >
          <AnimatePresence>
            {messages.map((message, index) => (
              <ChatMessage key={index} message={message} />
            ))}
            {isLoading && !messages.some(msg => msg.isTyping) && (
              <TypingIndicator key="loading-indicator" />
            )}
          </AnimatePresence>
          <div ref={messagesEndRef} />
        </div>

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={`mx-4 mb-3 text-xs text-center p-2 rounded-lg ${
                isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600'
              }`}
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Input */}
        <div className={`p-3 border-t ${isDark ? 'border-neutral-800' : 'border-neutral-100'}`}>
          <ChatInput
            input={input}
            setInput={setInput}
            isLoading={isLoading}
            onSubmit={handleSubmit}
            onStop={stopAnswering}
          />
        </div>
      </motion.div>
    </div>
  );
};